<template>
  <tooltip
    v-if="activeType"
    ref="tippyRef"
    trigger="click"
    interactive
    placement="bottom-end"
    theme="filter-popover"
    :append-to="appendToBody"
    :arrow="false"
    :offset="[0, 8]"
    @show="onShow"
    @hide="onHide"
  >
    <button
      type="button"
      data-cy="deal-activity-filters-toggle"
      class="flex h-6 items-center rounded-4 border bg-white text-xs leading-[18px] font-normal text-headline-new"
      :class="isOpen ? 'border-primary' : 'border-divider hover:bg-grey-hover'"
    >
      <span class="flex items-center space-x-2 px-2">
        <span>{{ $t("COMMON.FILTERS") }}</span>
        <span
          v-if="activeActivityFiltersCount"
          class="flex h-3 min-w-3 items-center justify-center rounded-full bg-medium-grey px-1 text-xxxs font-semibold uppercase text-headline-new"
          data-cy="deal-activity-filters-count"
        >
          {{ activeActivityFiltersCount }}
        </span>
        <icon-base
          v-if="!activeActivityFiltersCount"
          :icon="IconDropdownArrow"
          height="6"
          width="10"
          class="transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </span>
      <span
        v-if="activeActivityFiltersCount"
        class="flex h-full items-center border-l px-2 text-label hover:text-headline-new"
        :class="isOpen ? 'border-primary' : 'border-divider'"
        data-cy="deal-activity-filters-clear"
        @click.stop="handleClear"
      >
        <icon-base
          :icon="IconXSmall"
          height="8"
          width="8"
          view-box="0 0 10 9"
        />
      </span>
    </button>
    <template #content>
      <div
        class="flex gap-2 rounded-md bg-white p-2 shadow-main"
        data-cy="deal-activity-filters-panel"
      >
        <deal-activity-filter-submenu
          v-if="activeFilter"
          :key="activeFilter.key"
        >
          <component
            :is="activeFilter.component"
            v-bind="{
              ...activeFilter.props,
              toggleFromParent: !activeFilter.summary,
            }"
          >
            <template v-if="activeFilter.headerKey">
              {{ $t(activeFilter.headerKey) }}
            </template>
          </component>
        </deal-activity-filter-submenu>
        <div class="flex w-100 flex-col">
          <deal-activity-filter-row
            v-for="filter in filterConfigs"
            :key="filter.key"
            :label="$t(filter.labelKey)"
            :summary="filter.summary"
            :is-active="activeSubmenu === filter.key"
            @click="setSubmenu(filter.key)"
          />
        </div>
      </div>
    </template>
  </tooltip>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { CommunicationType } from "@/enums/communicationLogs";
import { useAuth } from "@/hooks/auth";
import { useCommunicationLogs } from "@/hooks/communicationLogs";

import FilterByAiAgent from "@/components/communicationLogs/filters/FilterByAiAgent.vue";
import FilterByAssignee from "@/components/communicationLogs/filters/FilterByAssignee.vue";
import FilterByDate from "@/components/communicationLogs/filters/FilterByDate.vue";
import FilterByPriority from "@/components/communicationLogs/filters/FilterByPriority.vue";
import FilterByStatus from "@/components/communicationLogs/filters/FilterByStatus.vue";
import FilterByEmailAutomation from "@/components/communicationLogs/filters/emailFilters/FilterByEmailAutomation.vue";
import FilterByEmailType from "@/components/communicationLogs/filters/emailFilters/FilterByEmailType.vue";
import FilterByMessageableStatus from "@/components/communicationLogs/filters/messageableFilters/FilterByMessageableStatus.vue";
import FilterBySmsType from "@/components/communicationLogs/filters/smsFilters/FilterBySmsType.vue";
import DealActivityFilterRow from "@/components/deals/dealActivity/DealActivityFilterRow.vue";
import DealActivityFilterSubmenu from "@/components/deals/dealActivity/DealActivityFilterSubmenu.vue";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconXSmall from "@/components/icons/IconXSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { Component } from "vue";

type FilterConfig = {
  key: string;
  labelKey: string;
  queryKeys: string[];
  component: Component;
  props: Record<string, unknown>;
  headerKey?: string;
  summary?: string;
};

type TippyHandle = { tippy?: { hide: () => void } };

const { activeType = null } = defineProps<{
  activeType?: CommunicationType | null;
}>();

const { t } = useI18n();
const route = useRoute();
const { isLendflowUser } = useAuth();
const { activeActivityFiltersCount, clearActivityFilters } =
  useCommunicationLogs();

const tippyRef = useTemplateRef<TippyHandle>("tippyRef");
const isOpen = ref(false);
const activeSubmenu = ref<string | null>(null);

const appendToBody = (reference: HTMLElement) => reference.ownerDocument.body;

const summaryFor = (keys: string[]) => {
  const total = keys.reduce((acc, key) => {
    const value = route.query[key];
    if (Array.isArray(value)) {
      return acc + value.length;
    }
    if (value) {
      return acc + 1;
    }
    return acc;
  }, 0);

  if (total === 0) {
    return undefined;
  }
  return t("ACTIVITY.FILTERS.SELECTED_SUMMARY", { count: total }, total);
};

const filterConfigsByType: Partial<Record<CommunicationType, FilterConfig[]>> =
  {
    [CommunicationType.note]: [
      {
        key: "note_date",
        labelKey: "ACTIVITY.FILTERS.SELECT_DATE",
        queryKeys: ["note_created_at"],
        component: FilterByDate,
        props: {
          placeholder: t("ACTIVITY.FILTERS.SELECT_DATE"),
          queryKey: "note_created_at",
        },
      },
    ],
    [CommunicationType.email]: [
      {
        key: "email_date",
        labelKey: "ACTIVITY.FILTERS.EMAILS.DATE",
        queryKeys: ["email_created_at"],
        component: FilterByDate,
        props: {
          placeholder: t("ACTIVITY.FILTERS.EMAILS.DATE"),
          queryKey: "email_created_at",
        },
      },
      {
        key: "email_sender",
        labelKey: "ACTIVITY.FILTERS.EMAILS.SENDER",
        queryKeys: ["sender_ids"],
        component: FilterByAssignee,
        props: {
          queryKey: "sender_ids",
          placeholderKey: "ACTIVITY.FILTERS.EMAILS.SENDER_PLACEHOLDER",
        },
        headerKey: "ACTIVITY.FILTERS.EMAILS.SENDER",
      },
      {
        key: "email_recipient",
        labelKey: "ACTIVITY.FILTERS.EMAILS.RECIPIENT",
        queryKeys: ["recipient_ids"],
        component: FilterByAssignee,
        props: {
          queryKey: "recipient_ids",
          type: "email_recipient",
          placeholderKey: "ACTIVITY.FILTERS.EMAILS.RECIPIENT_PLACEHOLDER",
        },
        headerKey: "ACTIVITY.FILTERS.EMAILS.RECIPIENT",
      },
      {
        key: "email_status",
        labelKey: "COMMON.STATUS",
        queryKeys: ["email_statuses[]"],
        component: FilterByMessageableStatus,
        props: { mode: "email" },
      },
      {
        key: "email_type",
        labelKey: "COMMON.TYPE",
        queryKeys: ["email_is_outgoing"],
        component: FilterByEmailType,
        props: {},
      },
      ...(isLendflowUser
        ? [
            {
              key: "email_automation",
              labelKey: "EMAIL_INTEGRATION.AUTOMATION.SELECT",
              queryKeys: ["email_is_automated"],
              component: FilterByEmailAutomation,
              props: {},
            },
          ]
        : []),
    ],
    [CommunicationType.sms]: [
      {
        key: "sms_date",
        labelKey: "ACTIVITY.FILTERS.SMS.DATE",
        queryKeys: ["sms_created_at"],
        component: FilterByDate,
        props: {
          placeholder: t("ACTIVITY.FILTERS.SMS.DATE"),
          queryKey: "sms_created_at",
        },
      },
      {
        key: "sms_sender",
        labelKey: "ACTIVITY.FILTERS.EMAILS.SENDER",
        queryKeys: ["sender_ids"],
        component: FilterByAssignee,
        props: {
          queryKey: "sender_ids",
          placeholderKey: "ACTIVITY.FILTERS.EMAILS.SENDER_PLACEHOLDER",
        },
        headerKey: "ACTIVITY.FILTERS.EMAILS.SENDER",
      },
      {
        key: "sms_status",
        labelKey: "COMMON.STATUS",
        queryKeys: ["sms_statuses[]"],
        component: FilterByMessageableStatus,
        props: { mode: "sms" },
      },
      {
        key: "sms_type",
        labelKey: "COMMON.TYPE",
        queryKeys: ["sms_types[]"],
        component: FilterBySmsType,
        props: {},
      },
    ],
    [CommunicationType.call]: [
      {
        key: "call_date",
        labelKey: "ACTIVITY.FILTERS.CALLS.DATE",
        queryKeys: ["call_session_created_at"],
        component: FilterByDate,
        props: {
          placeholder: t("ACTIVITY.FILTERS.CALLS.DATE"),
          queryKey: "call_session_created_at",
        },
      },
      {
        key: "call_caller",
        labelKey: "ACTIVITY.FILTERS.CALLS.CALLER",
        queryKeys: ["call_session_caller_ids"],
        component: FilterByAssignee,
        props: {
          queryKey: "call_session_caller_ids",
          placeholderKey: "ACTIVITY.FILTERS.CALLS.PLACEHOLDER",
        },
        headerKey: "ACTIVITY.FILTERS.CALLS.CALLER",
      },
    ],
    [CommunicationType.task]: [
      {
        key: "task_due",
        labelKey: "ACTIVITY.FILTERS.DUE_DATE",
        queryKeys: ["task_due_date"],
        component: FilterByDate,
        props: { placeholder: t("ACTIVITY.FILTERS.DUE_DATE") },
      },
      {
        key: "task_status",
        labelKey: "COMMON.STATUS",
        queryKeys: ["task_statuses"],
        component: FilterByStatus,
        props: {},
      },
      {
        key: "task_priority",
        labelKey: "ACTIVITY.FILTERS.PRIORITY",
        queryKeys: ["task_priority_level"],
        component: FilterByPriority,
        props: {},
      },
      {
        key: "task_assignee",
        labelKey: "ACTIVITY.FILTERS.ASSIGNEE.TITLE",
        queryKeys: ["assignee_ids"],
        component: FilterByAssignee,
        props: {
          queryKey: "assignee_ids",
          placeholderKey: "ACTIVITY.FILTERS.ASSIGNEE.PLACEHOLDER",
        },
        headerKey: "ACTIVITY.FILTERS.ASSIGNEE.TITLE",
      },
    ],
    [CommunicationType.ai_agent]: [
      {
        key: "ai_agent_initiator",
        labelKey: "ACTIVITY.FILTERS.AI_AGENTS.ACTIVITY_BY",
        queryKeys: ["ai_agent_initiator_ids"],
        component: FilterByAssignee,
        props: {
          queryKey: "ai_agent_initiator_ids",
          placeholderKey: "ACTIVITY.FILTERS.ASSIGNEE.PLACEHOLDER",
        },
        headerKey: "ACTIVITY.FILTERS.AI_AGENTS.ACTIVITY_BY",
      },
      {
        key: "ai_agent",
        labelKey: "ACTIVITY.FILTERS.AI_AGENTS.AGENT",
        queryKeys: ["ai_agent_ids"],
        component: FilterByAiAgent,
        props: {},
      },
      {
        key: "ai_agent_date",
        labelKey: "ACTIVITY.FILTERS.AI_AGENTS.DATE",
        queryKeys: ["ai_agent_created_at"],
        component: FilterByDate,
        props: {
          placeholder: t("ACTIVITY.FILTERS.AI_AGENTS.DATE"),
          queryKey: "ai_agent_created_at",
        },
      },
    ],
  };

const filterConfigs = computed<FilterConfig[]>(() => {
  if (!activeType) {
    return [];
  }
  const configs = filterConfigsByType[activeType];
  if (!configs) {
    return [];
  }
  return configs.map((config) => ({
    ...config,
    summary: summaryFor(config.queryKeys),
  }));
});

const activeFilter = computed(() =>
  filterConfigs.value.find((filter) => filter.key === activeSubmenu.value)
);

const onShow = () => {
  isOpen.value = true;
  activeSubmenu.value = filterConfigs.value[0]?.key ?? null;
};

const onHide = () => {
  isOpen.value = false;
  activeSubmenu.value = null;
};

const setSubmenu = (key: string) => {
  activeSubmenu.value = key;
};

const handleClear = async () => {
  await clearActivityFilters();
  tippyRef.value?.tippy?.hide();
};
</script>
